import { getFolder } from '@/lib/api/folderApi'
import type { GetFolderQuery } from '@/typings/domain'
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import type { RootState } from '../store'
import type { QueryState } from './queryStateType'

type ShallowFormFolder = Partial<NonNullable<GetFolderQuery['formFolder']>> & {
  token: string
  queryState: QueryState
}

const folderAdapter = createEntityAdapter<ShallowFormFolder, string>({
  selectId: model => model.token,
})

export const folderSlice = createSlice({
  name: 'forms',
  initialState: folderAdapter.getInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchFolderThunk.pending, (state, action) => {
        const folderToken = action.meta.arg.folderToken
        folderAdapter.upsertOne(state, {
          token: folderToken,
          queryState: {
            isLoading: true,
            loaded: state.entities[folderToken]?.queryState.loaded ?? false,
            error: null,
          },
        })
      })
      .addCase(fetchFolderThunk.fulfilled, (state, action) => {
        const fetchedFolder = action.payload
        folderAdapter.upsertOne(state, {
          ...fetchedFolder,
          queryState: {
            isLoading: false,
            loaded: true,
            error: null,
          },
        })
      })
      .addCase(fetchFolderThunk.rejected, (state, action) => {
        const folderToken = action.meta.arg.folderToken
        if (state.entities[folderToken]?.queryState) {
          state.entities[folderToken].queryState.isLoading = false
          state.entities[folderToken].queryState.error = action.error.message
        }
      })
  },
})

export const fetchFolderThunk = createAsyncThunk<NonNullable<GetFolderQuery['formFolder']>, { folderToken: string }>(
  'folders/fetchOne',
  async ({ folderToken }, thunkAPI) => {
    const { data, errors } = await getFolder({ folderToken })
    return (
      data.formFolder ??
      thunkAPI.rejectWithValue({
        message: errors?.[0]?.message,
      })
    )
  }
)

export const folderSelectors = folderAdapter.getSelectors<RootState>(state => state.folders)

export default folderSlice.reducer
